import { useEffect, useMemo, useState } from 'react';

import { fetchProjectNames, invalidateProjectNamesCache } from '@/actions/project';
import Autocomplete from '@/componentsv2/Autocomplete';
import type { AutocompleteOption } from '@/componentsv2/Autocomplete/types';
import Button from '@/componentsv2/Button';
import Dialog from '@/componentsv2/Dialog';
import UpdatingIndicator from '@/componentsv2/UpdatingIndicator';
import { hasErrorCode } from '@/core/api';
import ERROR_CODES from '@/core/apiErrorCodes';
import { moveExpertRequest } from '@/expertrequest/store';
import { useAppDispatch, useAppSelector } from '@/store';

interface EditAssociatedProjectDialogProps {
  open: boolean;
  onClose: () => void;
  expertRequestId: string;
  currentProjectId: string;
  // Required, though it may be undefined: a team-less project is normalised to ''
  // by the filter, so an omitted prop would be indistinguishable from "this
  // project genuinely has no team" and would silently offer only team-less
  // projects behind copy claiming there are none on this team.
  currentGroupId: string | undefined;
}

interface ProjectNameNode {
  id: string;
  name: string | null;
  group?: { id: string } | null;
}

// ENG-5314/ENG-5366: move an ER to another project WITHIN THE SAME TEAM. Moving
// it to a project in a different team is refused by the API outright (moving an
// ER's money to another customer in place is unsafe) — use "Make a Copy" instead.
// open-only matches the shared `names` cache other consumers populate, so the
// fetch isn't silently defeated by a prior open-only page. Note: the underlying
// query caps at 200 projects.
export default function EditAssociatedProjectDialog({
  open,
  onClose,
  expertRequestId,
  currentProjectId,
  currentGroupId,
}: EditAssociatedProjectDialogProps): JSX.Element {
  const dispatch = useAppDispatch();
  const projectsPage = useAppSelector((s) => s.projects.names);
  const [target, setTarget] = useState<AutocompleteOption | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setTarget(null);
      setError(null);
      dispatch(fetchProjectNames({ openOnly: true })).catch(() => {
        setError('Could not load projects. Please try again.');
      });
    }
  }, [open, dispatch]);

  const options = useMemo<AutocompleteOption[]>(
    () =>
      (projectsPage?.edges ?? [])
        .map((e: { node: ProjectNameNode }) => e.node)
        // Drop nameless projects — GraphQL `name` is nullable and a project with
        // no name isn't a sensible move target (and would render blank). Only
        // same-team projects are offered, since a cross-team move is refused
        // server-side. `group` is nullable and team-less projects are creatable, so
        // a missing team is normalised to '' to mirror the server exactly: it
        // compares util.NullID strings, where NULL is '', so team-less to team-less
        // is a same-team move and IS permitted. Filtering those out instead would
        // make the dialog a dead end for them.
        .filter(
          (n): n is ProjectNameNode & { name: string } =>
            Boolean(n && n.name && n.id !== currentProjectId) &&
            (n.group?.id ?? '') === (currentGroupId ?? '')
        )
        .map((n) => ({ id: n.id, name: n.name })),
    [projectsPage, currentProjectId, currentGroupId]
  );

  // `fetchProjectNames` only dispatches PROJECT__LIST (no loading flag), so
  // `resetAt` — set the first time the names collection resolves — is the real
  // load-completed signal. Gate the empty state and CTA on it so the dialog
  // doesn't flash "no other projects" before the fetch lands.
  const hasLoaded = Boolean(projectsPage?.resetAt);
  const hasOptions = options.length > 0;

  async function handleConfirm() {
    if (!target) return;
    setSubmitting(true);
    setError(null);
    try {
      await dispatch(moveExpertRequest(expertRequestId, target.id, currentProjectId));
      onClose();
    } catch (err) {
      // The picker only ever offers same-team projects, so this specific code
      // should be unreachable in practice — the one way it can still surface is
      // the project-names cache going stale relative to an out-of-band change.
      // Name it explicitly rather than a generic message, since "try again"
      // alone won't fix a stale cache; refetch so a retry has fresh data.
      if (hasErrorCode(err, ERROR_CODES.EXPERT_REQUEST_CROSS_GROUP_MOVE_NOT_SUPPORTED)) {
        setError('This project is no longer on this team. Refreshing the list.');
        // Clear the selection: the refetch is about to filter this project out of
        // the options, and leaving it selected keeps "Move request" enabled on a
        // value the picker no longer offers, so "try again" resubmits the same
        // server-refused id.
        setTarget(null);
        // Reset only the `names` collection this dialog reads, then re-fetch.
        // Do NOT use invalidateFetchedProjectsCache: that resets every
        // collection to empty, blanking the mounted ProjectDetails page
        // underneath this dialog (see moveExpertRequest's comment in
        // @/expertrequest/store). fetchProjectNames alone would skip the
        // query if the (stale) collection still looks fresh.
        await dispatch(invalidateProjectNamesCache());
        // The notice has to survive the reload, because the move did NOT happen and
        // that is the only thing telling the user so. Only the "refreshing" half
        // stops being true, so swap it for the settled wording rather than clearing
        // it: clearing would leave a repopulated picker with no trace of the
        // refusal. Both phases lead with the same sentence so the state the user
        // reads never contradicts itself mid-reload.
        dispatch(fetchProjectNames({ openOnly: true }))
          .then(() => {
            setError(
              'This project is no longer on this team. Pick another project to move this request.'
            );
          })
          .catch(() => {
            setError('Could not refresh the project list. Please try again.');
          });
      } else {
        setError('Something went wrong moving this request. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      disablePortal={false}
      aria-labelledby="er-edit-associated-project-title"
    >
      <div data-testid="er-edit-associated-project" className="flex flex-col gap-16 p-24">
        <h2 id="er-edit-associated-project-title" className="hd-5">
          Edit associated project
        </h2>

        {/* The body state is driven by the collection alone, never by `error`.
            Gating on `error` meant a refusal suppressed the spinner AND the empty
            state, so the dialog explained nothing while it reloaded and then sat
            blank if no same-team alternative came back. The error renders below,
            alongside whichever body state is current. */}
        {!hasLoaded ? (
          <div className="flex justify-center py-8">
            <UpdatingIndicator text="Loading projects…" />
          </div>
        ) : hasOptions ? (
          <>
            <p className="text-on-surface-secondary body-1">
              Move this expert request to another project on this team. Only same-team projects are
              shown — to hand it to a different team, use &quot;Make a Copy&quot; instead.
            </p>
            <Autocomplete
              options={options}
              value={target}
              onChange={(v) =>
                setTarget(
                  v && typeof v === 'object' && 'id' in v ? (v as AutocompleteOption) : null
                )
              }
              TextFieldProps={{ label: 'Project' }}
            />
          </>
        ) : (
          <p className="text-on-surface-secondary body-1">
            There are no other projects on this team to move this request to. To hand it to a
            different team, use &quot;Make a Copy&quot; instead.
          </p>
        )}

        {error ? <p className="text-alert-error body-2">{error}</p> : null}

        <div className="flex justify-end gap-8">
          <Button variant="tertiary" onClick={onClose}>
            {hasLoaded && hasOptions ? 'Cancel' : 'Close'}
          </Button>
          {hasLoaded && hasOptions && (
            <Button
              variant="primary"
              loading={submitting}
              disabled={!target || submitting}
              onClick={handleConfirm}
            >
              Move request
            </Button>
          )}
        </div>
      </div>
    </Dialog>
  );
}
